// TMOS Risk Categories Management Page
// Date: November 16, 2025
// Purpose: Platform Administrator page for managing global risk categories

import React, { useState } from 'react';
import { FlexibleCRUDTable } from '@/components/common/FlexibleCRUDTable';
import { ViewModal } from '@/components/common/ViewModal';
import { EditModal } from '@/components/common/EditModal';
import { riskCategoryEntity } from '@/config/entities/riskCategories';
import { createCRUDService } from '@/services/genericCRUD';
import { useLanguage } from '@/state/language/LanguageProvider';
import { ShieldExclamationIcon } from '@heroicons/react/24/outline';

export const RiskCategoriesPage: React.FC = () => {
  const { t } = useLanguage();
  const riskCategoryService = createCRUDService(riskCategoryEntity);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<any>(null);
  const [viewingCategory, setViewingCategory] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  
  // Category handlers
  const handleViewCategory = (category: any) => {
    setViewingCategory(category);
    setShowViewModal(true);
  };

  const handleCreateCategory = () => {
    setShowCreateModal(true);
  };

  const handleEditCategory = (category: any) => {
    setEditingCategory(category);
    setShowEditModal(true);
  };

  const handleDeleteCategory = async (category: any) => {
    // Confirmation is handled by FlexibleCRUDTable's modal before this is called
    try {
      await riskCategoryService.delete(category.id);
    } catch (error) {
      console.error('Error deleting category:', error);
      // Rethrow so FlexibleCRUDTable can roll back optimistic delete and show error toast
      throw error;
    }
  };

  const handleCreateCategorySubmit = async (data: any) => {
    setLoading(true);
    try {
      await riskCategoryService.create(data);
      setShowCreateModal(false);
      // Table will automatically refresh
    } catch (error) {
      console.error('Error creating category:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditCategorySubmit = async (data: any) => {
    setLoading(true);
    try {
      await riskCategoryService.update(editingCategory.id, data);
      setShowEditModal(false);
      setEditingCategory(null);
      // Table will automatically refresh
    } catch (error) {
      console.error('Error updating category:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelCategory = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setShowViewModal(false);
    setEditingCategory(null);
    setViewingCategory(null);
  };

  return (
    <div className="risk-categories-page">
      <div className="bg-white shadow">
        <div className="px-4 py-5 sm:px-6">
          <div className="flex items-center gap-3">
            <ShieldExclamationIcon className="w-8 h-8 text-[var(--color-primary-bg)]" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {t('risk.categories.title') || 'Risk Categories'}
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                {t('risk.categories.description') || 'Manage global risk categories available across all tenants'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6">
        <FlexibleCRUDTable
          entity={riskCategoryEntity}
          service={riskCategoryService}
          onView={handleViewCategory}
          onEdit={handleEditCategory}
          onDelete={handleDeleteCategory}
          onCreate={handleCreateCategory}
          searchPlaceholder={t('risk.categories.searchPlaceholder') || 'Search categories...'}
          showPagination={true}
          showSearch={true}
          showCreateButton={true}
        />
      </div>

      {/* Category Modals */}
      {showCreateModal && (
        <EditModal
          isOpen={showCreateModal}
          onClose={handleCancelCategory}
          title={`Create ${riskCategoryEntity.displayNameSingular}`}
          entity={riskCategoryEntity}
          onSubmit={handleCreateCategorySubmit}
          onCancel={handleCancelCategory}
          loading={loading}
        />
      )}

      {showEditModal && editingCategory && (
        <EditModal
          isOpen={showEditModal}
          onClose={handleCancelCategory}
          title={`Edit ${riskCategoryEntity.displayNameSingular}`}
          entity={riskCategoryEntity}
          initialData={editingCategory}
          onSubmit={handleEditCategorySubmit}
          onCancel={handleCancelCategory}
          loading={loading}
        />
      )}

      {showViewModal && viewingCategory && (
        <ViewModal
          isOpen={showViewModal}
          onClose={handleCancelCategory}
          title={`View ${riskCategoryEntity.displayNameSingular}`}
          data={viewingCategory}
          fields={riskCategoryEntity.fields.filter(f => !f.hidden).map(f => ({
            key: f.key || f.name || '',
            label: f.label,
            type: f.type
          }))}
        />
      )}
    </div>
  );
};

